"use client"

import { useRef, useEffect, useState, useCallback } from "react"

/*
 * COSMIC SHADER v4.0 - Dual Theme with Proper Blending
 *
 * Dark mode: Additive blending (bright on dark)
 * Light mode: Subtractive blending (dark on light)
 */

const fragmentShader = `#version 300 es
precision mediump float;

out vec4 fragColor;

uniform float uTime;
uniform vec2 uResolution;
uniform float uTheme; // 0.0 = dark, 1.0 = light

#define FC gl_FragCoord.xy
#define R uResolution
#define T uTime
#define MN min(R.x, R.y)

#define rot(a) mat2(cos((a) - vec4(0, 11, 33, 0)))
#define csqr(a) vec2(a.x * a.x - a.y * a.y, 2.0 * a.x * a.y)

// Dark theme palette
const vec3 DARK_BG = vec3(0.0);
const vec3 DARK_PRIMARY = vec3(0.659, 0.0, 0.824);    // Brand purple
const vec3 DARK_SECONDARY = vec3(0.85, 0.4, 1.0);     // Light purple
const vec3 DARK_ACCENT = vec3(0.22, 0.0, 0.28);       // Deep purple

// Light theme palette - branco com roxo da marca
const vec3 LIGHT_BG = vec3(1.0);                      // Branco puro
const vec3 LIGHT_PRIMARY = vec3(0.58, 0.22, 0.93);    // #9333ea - purple-600
const vec3 LIGHT_SECONDARY = vec3(0.66, 0.33, 0.97);  // #a855f7 - purple-500
const vec3 LIGHT_ACCENT = vec3(0.76, 0.52, 0.98);     // #c084fc - purple-400

float rnd(vec3 p) {
  p = fract(p * vec3(12.9898, 78.233, 156.34));
  p += dot(p, p + 34.56);
  return fract(p.x * p.y * p.z);
}

float swirls(vec3 p) {
  float d = 0.0;
  vec3 c = p;
  for (float i = 0.0; i < 4.0; i++) {
    p = 0.75 * abs(p) / dot(p, p) - 0.75;
    p.yz = csqr(p.yz);
    p = p.zxy;
    d += exp(-18.0 * abs(dot(p, c)));
  }
  return d;
}

float swirls2(vec3 p) {
  float d = 0.0;
  vec3 c = p * 1.5;
  for (float i = 0.0; i < 3.0; i++) {
    p = 0.65 * abs(p) / dot(p, p) - 0.65;
    p.xz = csqr(p.xz);
    p = p.yzx;
    d += exp(-22.0 * abs(dot(p, c)));
  }
  return d;
}

vec3 marchDark(vec3 origin, vec3 rd) {
  float d = 0.3;
  float t = 0.0;
  float c = 0.0;
  float c2 = 0.0;
  float k = mix(0.85, 1.0, rnd(rd));
  float maxDist = length(origin) - 1.0;
  vec3 col = vec3(0.0);

  for (float i = 0.0; i < 50.0; i++) {
    t += d * exp(-2.0 * c) * k;
    vec3 pos = origin + rd * t;
    c = swirls(pos);
    c2 = swirls2(pos * 0.8 + vec3(T * 0.02));
    if (t < 0.05 || t > maxDist) break;
    // Additive blending for dark mode
    col += DARK_PRIMARY * c * c * 0.009;
    col += DARK_SECONDARY * c2 * 0.006;
    col += DARK_ACCENT * (c + c2) * 0.003;
  }
  return col;
}

vec3 marchLight(vec3 origin, vec3 rd) {
  float d = 0.3;
  float t = 0.0;
  float c = 0.0;
  float c2 = 0.0;
  float k = mix(0.85, 1.0, rnd(rd));
  float maxDist = length(origin) - 1.0;
  vec3 col = vec3(0.0);

  for (float i = 0.0; i < 50.0; i++) {
    t += d * exp(-2.0 * c) * k;
    vec3 pos = origin + rd * t;
    c = swirls(pos);
    c2 = swirls2(pos * 0.8 + vec3(T * 0.02));
    if (t < 0.05 || t > maxDist) break;
    // Same intensity as dark mode
    col += LIGHT_PRIMARY * c * c * 0.009;
    col += LIGHT_SECONDARY * c2 * 0.006;
    col += LIGHT_ACCENT * (c + c2) * 0.003;
  }
  return col;
}

void cam(inout vec3 p) {
  p.yz *= rot(-T * 0.02);
  p.xz *= rot(T * 0.012);
}

void main() {
  vec2 uv = (FC - 0.5 * R) / MN;
  vec3 origin = vec3(0.0, 0.0, -14.0);
  vec3 rd = normalize(vec3(uv, 1.2));

  cam(origin);
  cam(rd);

  vec3 col;
  vec3 bgColor;

  if (uTheme < 0.5) {
    // Dark mode
    bgColor = DARK_BG;
    col = marchDark(origin, rd);
    col = smoothstep(-0.12, 1.0, col);
    col = pow(col, vec3(0.95));
  } else {
    // Light mode - usa intensidade como máscara para mostrar roxo
    bgColor = LIGHT_BG;
    vec3 effect = marchDark(origin, rd);
    effect = smoothstep(-0.12, 1.0, effect);
    effect = pow(effect, vec3(0.95));
    // Intensidade total do efeito (luminosidade)
    float intensity = (effect.r + effect.g + effect.b) / 3.0;
    // Mistura branco → roxo baseado na intensidade
    col = mix(bgColor, DARK_PRIMARY, intensity * 1.5);
  }

  // Center fade (hollow center) - ambos os modos
  float centerDist = length(uv);
  float centerFade = smoothstep(0.0, 0.55, centerDist);
  col = mix(bgColor, col, centerFade);

  // Fade in animation
  float fadeIn = min((T - 0.3) * 0.4, 1.0);
  col = mix(bgColor, col, fadeIn);

  // Vignette - igual para ambos
  vec2 vigUV = FC / R * 2.0 - 1.0;
  vigUV *= 0.55;
  float vignette = pow(dot(vigUV, vigUV), 1.4);
  col = mix(col, bgColor, vignette * 0.6);

  // Color bounds
  if (uTheme < 0.5) {
    col = max(col, vec3(0.008, 0.0, 0.012));
  }
  // Light mode: sem clamp - deixa os roxos aparecerem

  fragColor = vec4(col, 1.0);
}
`

const vertexShader = `#version 300 es
precision mediump float;
in vec4 position;
void main() {
  gl_Position = position;
}
`

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function isLowPowerDevice(): boolean {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
         navigator.maxTouchPoints > 2 ||
         navigator.hardwareConcurrency <= 4
}

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
         (navigator.maxTouchPoints > 0 && window.innerWidth < 768)
}

export function CosmicShader() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [mounted, setMounted] = useState(false)
  const [useStaticFallback, setUseStaticFallback] = useState(false)
  // Start with 'light' since we only use light theme now
  const [theme, setTheme] = useState<'dark' | 'light'>('light')
  const animationRef = useRef<number>(0)
  const glRef = useRef<WebGL2RenderingContext | null>(null)
  const programRef = useRef<WebGLProgram | null>(null)
  const startTimeRef = useRef(0)
  const lastFrameRef = useRef(0)
  const themeRef = useRef(1.0) // 1.0 = light theme

  const initWebGL = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return false

    // Use static fallback on mobile devices - WebGL shader is too heavy
    if (prefersReducedMotion() || isMobileDevice()) {
      setUseStaticFallback(true)
      return false
    }

    const gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      powerPreference: "low-power",
      preserveDrawingBuffer: false,
      failIfMajorPerformanceCaveat: true
    })

    if (!gl) {
      setUseStaticFallback(true)
      return false
    }

    glRef.current = gl

    const vs = gl.createShader(gl.VERTEX_SHADER)!
    gl.shaderSource(vs, vertexShader)
    gl.compileShader(vs)

    if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
      setUseStaticFallback(true)
      return false
    }

    const fs = gl.createShader(gl.FRAGMENT_SHADER)!
    gl.shaderSource(fs, fragmentShader)
    gl.compileShader(fs)

    if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
      setUseStaticFallback(true)
      return false
    }

    const program = gl.createProgram()!
    gl.attachShader(program, vs)
    gl.attachShader(program, fs)
    gl.linkProgram(program)

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      setUseStaticFallback(true)
      return false
    }

    programRef.current = program

    const vertices = new Float32Array([-1, 1, -1, -1, 1, 1, 1, -1])
    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW)

    const position = gl.getAttribLocation(program, "position")
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

    startTimeRef.current = performance.now()

    return true
  }, [])

  const resize = useCallback(() => {
    const canvas = canvasRef.current
    const gl = glRef.current
    if (!canvas || !gl) return

    const maxDpr = isLowPowerDevice() ? 1.0 : 1.25
    const dpr = Math.min(window.devicePixelRatio, maxDpr)
    const width = window.innerWidth
    const height = window.innerHeight

    canvas.width = Math.floor(width * dpr)
    canvas.height = Math.floor(height * dpr)
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`

    gl.viewport(0, 0, canvas.width, canvas.height)
  }, [])

  const render = useCallback(() => {
    const gl = glRef.current
    const program = programRef.current
    const canvas = canvasRef.current

    if (!gl || !program || !canvas) return

    const now = performance.now()

    if (now - lastFrameRef.current < 33) {
      animationRef.current = requestAnimationFrame(render)
      return
    }
    lastFrameRef.current = now

    const time = (now - startTimeRef.current) * 0.001
    const themeVal = themeRef.current

    // Clear color based on theme
    if (themeVal < 0.5) {
      gl.clearColor(0, 0, 0, 1)
    } else {
      gl.clearColor(1, 1, 1, 1) // Branco
    }

    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.useProgram(program)

    gl.uniform1f(gl.getUniformLocation(program, "uTime"), time)
    gl.uniform2f(gl.getUniformLocation(program, "uResolution"), canvas.width, canvas.height)
    gl.uniform1f(gl.getUniformLocation(program, "uTheme"), themeVal)

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

    animationRef.current = requestAnimationFrame(render)
  }, [])

  useEffect(() => {
    setMounted(true)
    // Always use light theme
    setTheme('light')
  }, [])

  useEffect(() => {
    if (!mounted) return

    themeRef.current = theme === 'light' ? 1.0 : 0.0

    const initFn = () => {
      const success = initWebGL()
      if (!success) return
      resize()
      render()
    }

    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number })
        .requestIdleCallback(initFn, { timeout: 1000 })
    } else {
      setTimeout(initFn, 100)
    }

    const handleResize = () => resize()
    window.addEventListener("resize", handleResize, { passive: true })

    return () => {
      window.removeEventListener("resize", handleResize)
      cancelAnimationFrame(animationRef.current)
      if (glRef.current) {
        const ext = glRef.current.getExtension('WEBGL_lose_context')
        if (ext) ext.loseContext()
      }
    }
  }, [mounted, theme, initWebGL, resize, render])

  // Don't render anything on server - let CSS handle initial background
  if (!mounted) {
    return null
  }

  // Static fallback for reduced motion, WebGL failure, or mobile devices
  if (useStaticFallback) {
    const isDark = theme === 'dark'
    return (
      <div className="fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{
            background: isDark
              ? 'radial-gradient(ellipse at center, #1a0025 0%, #0a0012 40%, #000000 100%)'
              : 'radial-gradient(ellipse at center, #f5f0ff 0%, #faf8ff 40%, #ffffff 100%)'
          }}
        />
        {/* Gradient orbs for mobile - opacity-only animation, no blur repaints */}
        <div
          className="absolute w-[600px] h-[600px] rounded-full animate-fade-slow"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(168,85,247,0.25) 0%, transparent 60%)'
              : 'radial-gradient(circle, rgba(147,51,234,0.12) 0%, transparent 60%)',
            top: '10%',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full animate-fade-slow"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(192,132,252,0.2) 0%, transparent 60%)'
              : 'radial-gradient(circle, rgba(168,85,247,0.08) 0%, transparent 60%)',
            bottom: '20%',
            right: '-10%',
            animationDelay: '3s',
          }}
        />
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ background: theme === 'dark' ? "#000" : "#fff" }}
        aria-hidden="true"
      />
    </div>
  )
}
